import { useState } from 'react';
import { authApi } from '@/auth/authApi';
import { useAuthStore } from '@/auth/authStore';
import { useUserStore } from '@/stores/userStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useT } from '@/i18n/useI18n';
import { containsProfanity } from '@shared/profanity';

type Step = 'welcome' | 'name' | 'shout';

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  background: 'rgba(0,0,0,0.35)',
  border: '1px solid var(--hairline)',
  borderRadius: 8,
  padding: '10px 12px',
  color: 'var(--ink)',
  fontFamily: 'var(--f-body)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

/**
 * First-run onboarding for new players: a quick welcome, then guided setup of
 * the display name and victory shout (both free on the first change). Shown
 * once — completion flips settingsStore.onboarded. Trigger lives in HomeScreen.
 */
export function OnboardingModal({ onDone }: { onDone: () => void }) {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const setUser = useAuthStore((s) => s.setUser);
  const setProfile = useUserStore((s) => s.setProfile);
  const setOnboarded = useSettingsStore((s) => s.setOnboarded);
  const t = useT();

  const [step, setStep] = useState<Step>('welcome');
  const [name, setName] = useState(user?.displayName ?? '');
  const [shout, setShout] = useState(user?.victoryShout ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const finish = () => { setOnboarded(true); onDone(); };

  const nameBad = name.trim().length > 0 && containsProfanity(name.trim());
  const shoutBad = shout.trim().length > 0 && containsProfanity(shout.trim());

  const saveName = async () => {
    const next = name.trim();
    if (!next) { setErr(t('home.display_name_ph')); return; }
    if (nameBad) { setErr(t('profanity.blocked')); return; }
    setBusy(true); setErr(null);
    try {
      if (next !== user?.displayName) {
        const { user: fresh } = await authApi.updateDisplayName(token!, next);
        setUser(fresh);
        setProfile({ name: fresh.displayName, gems: fresh.gems, coins: fresh.coins });
      }
      setStep('shout');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const saveShout = async () => {
    const next = shout.trim();
    if (shoutBad) { setErr(t('profanity.blocked')); return; }
    setBusy(true); setErr(null);
    try {
      if (next && next !== (user?.victoryShout ?? '')) {
        const { user: fresh } = await authApi.updateVictoryShout(token!, next);
        setUser(fresh);
        setProfile({ gems: fresh.gems, coins: fresh.coins });
      }
      finish();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(6px)',
        zIndex: 650,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'max(env(safe-area-inset-top, 0px), 18px) 18px max(env(safe-area-inset-bottom, 0px), 18px)',
      }}
    >
      <div
        className="card"
        style={{ width: 340, maxWidth: '100%', padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        {/* step dots */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          {(['welcome', 'name', 'shout'] as Step[]).map((s) => (
            <span
              key={s}
              style={{
                width: 7, height: 7, borderRadius: '50%',
                background: s === step ? 'var(--c-cyan)' : 'var(--hairline)',
              }}
            />
          ))}
        </div>

        {step === 'welcome' && (
          <>
            <div className="display" style={{ fontSize: 19, letterSpacing: '0.04em', color: 'var(--c-cyan)' }}>
              {t('onboarding.welcome_title')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-muted)', lineHeight: 1.5 }}>
              {t('onboarding.welcome_body')}
            </div>
            <button className="btn btn-primary" onClick={() => setStep('name')}>
              {t('onboarding.next')}
            </button>
          </>
        )}

        {step === 'name' && (
          <>
            <div className="display" style={{ fontSize: 18, letterSpacing: '0.04em', color: 'var(--c-cyan)' }}>
              {t('onboarding.name_title')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-muted)', lineHeight: 1.45 }}>
              {t('onboarding.name_body')}
            </div>
            <input
              style={INPUT_STYLE}
              value={name}
              maxLength={24}
              placeholder={t('home.display_name_ph')}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void saveName(); }}
              autoFocus
            />
            {nameBad && <div style={{ fontSize: 11, color: 'var(--c-red)' }}>{t('profanity.blocked')}</div>}
            {err && <div style={{ fontSize: 11, color: 'var(--c-red)' }}>{err}</div>}
            <button className="btn btn-primary" onClick={saveName} disabled={busy || nameBad}>
              {t('onboarding.next')}
            </button>
          </>
        )}

        {step === 'shout' && (
          <>
            <div className="display" style={{ fontSize: 18, letterSpacing: '0.04em', color: 'var(--c-cyan)' }}>
              {t('onboarding.shout_title')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-muted)', lineHeight: 1.45 }}>
              {t('onboarding.shout_body')}
            </div>
            <input
              style={INPUT_STYLE}
              value={shout}
              maxLength={40}
              placeholder={t('home.victory_shout_ph')}
              onChange={(e) => setShout(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void saveShout(); }}
              autoFocus
            />
            {shoutBad && <div style={{ fontSize: 11, color: 'var(--c-red)' }}>{t('profanity.blocked')}</div>}
            {err && <div style={{ fontSize: 11, color: 'var(--c-red)' }}>{err}</div>}
            <button className="btn btn-primary" onClick={saveShout} disabled={busy || shoutBad}>
              {t('onboarding.finish')}
            </button>
            <button
              className="btn btn-ghost"
              onClick={finish}
              disabled={busy}
              style={{ marginTop: -6 }}
            >
              {t('onboarding.skip')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
